/**
 * Build a DB Schema using a given RecordDef
 * 
 * All plain section fields go to the main table
 * Each fields in a table section go to a table specific for that section,
 * related to the main table by its UUID.
 * 
 * @created: MAZito - 2-Abr-2021
 */
import { toVarName, slugify } from '@/helpers'
import { mapAllSections } from './section-mappers'


export function createAndAlterTablesDDL(recordDef) {
  /**
   * Creates all DLL statements from a given RecordDef.
   * 
   * IMPORTANT: we generate a CREATE TABLE and also ADD COLUMN and ALTER COLUMN 
   * statements, for each table an all its columns. In this way we can allways 
   * modify the schema without destroying the existing tables and its data.
   * 
   *  CREATE TABLE table_name();
   *  ALTER TABLE table_name ADD COLUMN new_column_name data_type;
   *  ALTER TABLE table_name ALTER COLUMN column_name TYPE data_type;
   * 
   * If the schema is empty, the tables will be created as usual, but if the
   * schema already exists, the CREATE TABLE will fail and existent tables will 
   * not be recreated.
   * 
   * In the same way the ADD/ALTER COLUMN statements will fail if the columns 
   * already exist or the data_type is identical, but if a new column is added 
   * or a data_type is changed the ADD/ALTER statement will run and apply 
   * the changes.
  */
  const dbname = databaseName(recordDef.account_id);
  
  const tables = extractTables(recordDef);

  let DDL = tables.map((tb) => {
    return(createTableDDL(tb) + addColumnsDDL(tb) + alterColumnsDDL(tb));
  }).join("\n")

  return DDL;
}


function createTableDDL(tb) {
  // columns for CREATE TABLE
  let colsDDL = tb.columns.map((col) => {
    return `  ${col.name} ${col.sqltype}`
  }).join(',\n');

  // we dont drop the table, if it already exists it will fail gracefully
  let DDL = `\n\nCREATE TABLE IF NOT EXISTS ${tb.name} (\n${colsDDL}\n);\n\n`
  return DDL;
}


function addColumnsDDL(tb) {
  let DDL = tb.columns.map((col) => {
    return `ALTER TABLE ${tb.name} ADD COLUMN ${col.name} ${col.sqltype}`
  }).join(';\n');
  return DDL+";\n";
}


function alterColumnsDDL(tb) {
  let DDL = tb.columns.map((col) => {
    return `ALTER TABLE ${tb.name} ALTER COLUMN ${col.name} TYPE ${col.sqltype}`
  }).join(';\n');
  return DDL+";\n";
}


export function createDatabaseDDL(accountId) {
  const name = databaseName(accountId);
  // const drop = `DROP DATABASE IF EXISTS ${name};\n\n`;
  const create = `CREATE DATABASE ${name} WITH OWNER = postgres ENCODING = 'UTF8';\n\n`
  const use = `\\connect  ${name};\n\n`;
  return (create+use);
}


export function databaseName(accountId) {
  return ('mb'+accountId+'data')
}


function extractTables(recordDef) {
  /**
   * Extracts the basic tables data from the RecordDef.
   * It is used by the DDL functions wich create the actual SQL DDL.
   * 
   * @recordDef: the recordDef we will use to create the tables.
   * @returns: an array of tables with basic table info, of the form:
   *  [ 
   *    {
   *      name: "table_name",
   *      columns: [ { name: "col_name", sqltype: "col_type" }, ... ]
   *    }, ...
   *  ]
   */
  let tables = [];

  // mapp the RecordDef sections and extract basic data (names an types)
  let mappers = mapAllSections(recordDef.meta)

  // there must be only one table with all the plain sections fields
  tables.push(mappers.plain); 

  // and there must be one table for each table section
  tables = tables.concat(mappers.tables)

  // the base table name for all tables using the recordDef title
  let baseName = slugify(recordDef.title);

  tables = tables.map((tb) => {

    let isTbType = (tb.type === 'table');

    // if its a table section, append section name to base table name
    let name = baseName + (isTbType ? '_'+tb.name : '');

    // this are the default columns present in every table
    let cols = [
      { "name": "uid", "sqltype": "varchar(64)"},
      { "name": "r_def_id", "sqltype": "integer"},
      { "name":"r_title", "sqltype": "varchar(256)"},
      { "name":"r_created_utc", "sqltype": "timestamp",},
    ]; 

    // if its a table section we need to add a special "row_index" column 
    cols = cols.concat((!isTbType) 
      ? [] 
      : [{ "name": "row_index", "sqltype": "integer"}]
    ); 

    // add the rest of the Table columns
    cols = cols.concat(tb.cols.map((col) => {
      return { "name": col.name, "sqltype": col.sqltype }
    }));

    return {
      name: name,
      columns: cols
    }
  })

  return tables;
}
